import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { createSelectors } from ".";
import { useStateActions } from "./hooks/useStateActions";

export type DBTForm = {
  github_dbt_folder_link: string;
  github_pat: string;
};

type SessionStoreState = {
  interruptAction: string | null;
  threadId: string;
  assistantId: string;
  activeRunId: string | undefined;
  messageValue: string;
  streamMessage: string;
  messages: any[];
  outputs: any[];
  steps: any[];
  reasoningEnabled: boolean;
  streaming: boolean;
  loading: boolean;
  showDBTForm: boolean;
  dbtForm: DBTForm;
  sidebar: boolean;
  refetch: boolean;
  thread: any;
};

type SessionStoreActions = {
  actions: {
    setLoading: (loading: boolean) => void;
    setStream: (streaming: boolean) => void;
    setMessageValue: (messageValue: string) => void;
    setActiveRunId: (activeRunId: string | undefined) => void;
    setMessages: (messages: any[]) => void;
    setStreamMessage: (streamMessage: string) => void;
    storeMessageAsStream: (newMessage: any) => void;
    fetchNewThread: (
      threadId: string,
      assistantId: string,
      values: any
    ) => void;
    updateOutput: (outputs: any[]) => void;
    resetState: () => void;
    appendNewStep: (newStep: string) => void;
    setShowDBTForm: (showDBTForm: boolean) => void;
    updateDBTFormField: (field: keyof DBTForm, value: string) => void;
    setSidebar: (sidebar: boolean) => void;
    setRefetch: (refetch: boolean) => void;
    setThread: (thread: any) => void;
    setInterruptAction: (action: string | null) => void;
    clearSteps: () => void;
    toggleReasoningEnabled: () => void;
  };
};

type SessionStore = SessionStoreState & SessionStoreActions;

const initialState: SessionStoreState = {
  interruptAction: null,
  threadId: "",
  assistantId: "",
  activeRunId: undefined,
  messageValue: "",
  outputs: [],
  messages: [],
  steps: [],
  reasoningEnabled: false,
  streaming: false,
  loading: false,
  streamMessage: "",
  showDBTForm: false,
  dbtForm: {
    github_dbt_folder_link: "",
    github_pat: "",
  },
  sidebar: false,
  refetch: false,
  thread: null,
};

const useSessionStoreBase = create<SessionStore>()(
  persist(
    (set, get) => ({
      ...initialState,
      actions: {
        setStreamMessage: (streamMessage) => {
          set({ streamMessage });
        },
        setLoading: (loading) => {
          set({ loading });
        },
        setStream: (streaming) => {
          set({ streaming });
        },
        setMessageValue: (messageValue) => {
          set({ messageValue });
        },

        setActiveRunId: (activeRunId) => {
          set({ activeRunId });
        },
        setMessages: (messages) => {
          set({ messages });
        },
        resetState: () =>
          set((state) => ({
            ...initialState,
            reasoningEnabled: state.reasoningEnabled, // preserve persisted value
          })),
        storeMessageAsStream: async (newMessage) => {
          set((state) => ({
            messages: [...state.messages, { ...newMessage, content: "" }],
          }));

          for (let i = 0; i < newMessage.content.length; i++) {
            await new Promise((resolve) => setTimeout(resolve, 5));
            set((state) => ({
              messages: state.messages.map((message) => {
                if (message.id === newMessage.id)
                  return {
                    ...message,
                    content: message.content + newMessage.content[i],
                  };

                return message;
              }),
            }));
          }
        },
        fetchNewThread: (
          threadId: string,
          assistantId: string,
          values: any
        ) => {
          set({
            threadId,
            assistantId,
            outputs: values?.outputs || [],
            messages: values?.messages || [],
          });
        },
        updateOutput: (outputs: any[]) => {
          set((state) => ({
            outputs: outputs.length ? outputs : state.outputs,
          }));
        },
        appendNewStep: (newStep) => {
          set((state) => ({
            steps: [...state.steps, newStep],
          }));
        },
        clearSteps: () => {
          set({
            steps: [],
          });
        },
        toggleReasoningEnabled: () => {
          set((state) => ({
            reasoningEnabled: !state.reasoningEnabled,
          }));
        },
        setShowDBTForm: (showDBTForm) => {
          set({ showDBTForm });
        },
        updateDBTFormField: (field, value) => {
          set((state) => ({
            dbtForm: { ...state.dbtForm, [field]: value },
          }));
        },
        setSidebar: (sidebar) => {
          set({ sidebar });
        },
        setRefetch: (refetch) => {
          set({ refetch });
        },
        setThread: (thread) => {
          set({ thread });
        },
        setInterruptAction: (action) => {
          set({ interruptAction: action });
        },
      },
    }),
    {
      name: "session-store", // unique name for localStorage key
      storage: createJSONStorage(() => localStorage), // use localStorage
      partialize: (state) => ({ reasoningEnabled: state.reasoningEnabled }), // persist only reasoningEnabled
    }
  )
);

export const useSessionStore = createSelectors(useSessionStoreBase);
export const useSessionActions = () => useStateActions(useSessionStore);
